import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Input, Button, Typography, Space, message, Select } from 'antd'
import { Gift, Phone, CheckCircle } from 'lucide-react'
import { useGetOTP } from '../../api/useGetOTP'
import { useVerifyOTP } from '../../api/useVerifyOTP'

interface PhoneVerificationModalProps {
  isOpen: boolean
  onClose: () => void
  userEmail?: string
}

const MAX_RESEND_ATTEMPTS = 2
const RESEND_TIMER_DURATION_SECONDS = 30

// Country data for dropdown
const COUNTRIES = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
  { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱' },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420', flag: '🇨🇿' },
  { code: 'HU', name: 'Hungary', dialCode: '+36', flag: '🇭🇺' },
  { code: 'RO', name: 'Romania', dialCode: '+40', flag: '🇷🇴' },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359', flag: '🇧🇬' },
  { code: 'HR', name: 'Croatia', dialCode: '+385', flag: '🇭🇷' },
  { code: 'SI', name: 'Slovenia', dialCode: '+386', flag: '🇸🇮' },
  { code: 'SK', name: 'Slovakia', dialCode: '+421', flag: '🇸🇰' },
  { code: 'LT', name: 'Lithuania', dialCode: '+370', flag: '🇱🇹' },
  { code: 'LV', name: 'Latvia', dialCode: '+371', flag: '🇱🇻' },
  { code: 'EE', name: 'Estonia', dialCode: '+372', flag: '🇪🇪' },
  { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'GR', name: 'Greece', dialCode: '+30', flag: '🇬🇷' },
  { code: 'CY', name: 'Cyprus', dialCode: '+357', flag: '🇨🇾' },
  { code: 'MT', name: 'Malta', dialCode: '+356', flag: '🇲🇹' },
  { code: 'LU', name: 'Luxembourg', dialCode: '+352', flag: '🇱🇺' },
  { code: 'IS', name: 'Iceland', dialCode: '+354', flag: '🇮🇸' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380', flag: '🇺🇦' },
  { code: 'BY', name: 'Belarus', dialCode: '+375', flag: '🇧🇾' },
  { code: 'MD', name: 'Moldova', dialCode: '+373', flag: '🇲🇩' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
  { code: 'QA', name: 'Qatar', dialCode: '+974', flag: '🇶🇦' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman', dialCode: '+968', flag: '🇴🇲' },
  { code: 'JO', name: 'Jordan', dialCode: '+962', flag: '🇯🇴' },
  { code: 'LB', name: 'Lebanon', dialCode: '+961', flag: '🇱🇧' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'MA', name: 'Morocco', dialCode: '+212', flag: '🇲🇦' },
  { code: 'TN', name: 'Tunisia', dialCode: '+216', flag: '🇹🇳' },
  { code: 'DZ', name: 'Algeria', dialCode: '+213', flag: '🇩🇿' },
  { code: 'LY', name: 'Libya', dialCode: '+218', flag: '🇱🇾' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251', flag: '🇪🇹' },
  { code: 'UG', name: 'Uganda', dialCode: '+256', flag: '🇺🇬' },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255', flag: '🇹🇿' },
  { code: 'MW', name: 'Malawi', dialCode: '+265', flag: '🇲🇼' },
  { code: 'ZM', name: 'Zambia', dialCode: '+260', flag: '🇿🇲' },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263', flag: '🇿🇼' },
  { code: 'BW', name: 'Botswana', dialCode: '+267', flag: '🇧🇼' },
  { code: 'NA', name: 'Namibia', dialCode: '+264', flag: '🇳🇦' },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268', flag: '🇸🇿' },
  { code: 'LS', name: 'Lesotho', dialCode: '+266', flag: '🇱🇸' },
  { code: 'MG', name: 'Madagascar', dialCode: '+261', flag: '🇲🇬' },
  { code: 'MU', name: 'Mauritius', dialCode: '+230', flag: '🇲🇺' },
  { code: 'SC', name: 'Seychelles', dialCode: '+248', flag: '🇸🇨' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'PE', name: 'Peru', dialCode: '+51', flag: '🇵🇪' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
  { code: 'EC', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
  { code: 'BO', name: 'Bolivia', dialCode: '+591', flag: '🇧🇴' },
  { code: 'PY', name: 'Paraguay', dialCode: '+595', flag: '🇵🇾' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
  { code: 'GY', name: 'Guyana', dialCode: '+592', flag: '🇬🇾' },
  { code: 'SR', name: 'Suriname', dialCode: '+597', flag: '🇸🇷' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'KP', name: 'North Korea', dialCode: '+850', flag: '🇰🇵' },
  { code: 'MN', name: 'Mongolia', dialCode: '+976', flag: '🇲🇳' },
  { code: 'KZ', name: 'Kazakhstan', dialCode: '+7', flag: '🇰🇿' },
  { code: 'UZ', name: 'Uzbekistan', dialCode: '+998', flag: '🇺🇿' },
  { code: 'TM', name: 'Turkmenistan', dialCode: '+993', flag: '🇹🇲' },
  { code: 'TJ', name: 'Tajikistan', dialCode: '+992', flag: '🇹🇯' },
  { code: 'KG', name: 'Kyrgyzstan', dialCode: '+996', flag: '🇰🇬' },
  { code: 'AF', name: 'Afghanistan', dialCode: '+93', flag: '🇦🇫' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
  { code: 'MV', name: 'Maldives', dialCode: '+960', flag: '🇲🇻' },
  { code: 'BT', name: 'Bhutan', dialCode: '+975', flag: '🇧🇹' },
  { code: 'NP', name: 'Nepal', dialCode: '+977', flag: '🇳🇵' },
  { code: 'MM', name: 'Myanmar', dialCode: '+95', flag: '🇲🇲' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { code: 'LA', name: 'Laos', dialCode: '+856', flag: '🇱🇦' },
  { code: 'KH', name: 'Cambodia', dialCode: '+855', flag: '🇰🇭' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
  { code: 'BN', name: 'Brunei', dialCode: '+673', flag: '🇧🇳' },
  { code: 'TL', name: 'East Timor', dialCode: '+670', flag: '🇹🇱' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
  { code: 'TW', name: 'Taiwan', dialCode: '+886', flag: '🇹🇼' },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852', flag: '🇭🇰' },
  { code: 'MO', name: 'Macau', dialCode: '+853', flag: '🇲🇴' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
  { code: 'FJ', name: 'Fiji', dialCode: '+679', flag: '🇫🇯' },
  { code: 'PG', name: 'Papua New Guinea', dialCode: '+675', flag: '🇵🇬' },
  { code: 'SB', name: 'Solomon Islands', dialCode: '+677', flag: '🇸🇧' },
  { code: 'VU', name: 'Vanuatu', dialCode: '+678', flag: '🇻🇺' },
]

const DEFAULT_COUNTRY = COUNTRIES[0]

function getOrCreateVisitorFingerprint(): string {
  try {
    const existing = localStorage.getItem('fpVisitorId')
    if (existing) return existing
    const array = new Uint8Array(16)
    window.crypto.getRandomValues(array)
    const randomId = Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
    localStorage.setItem('fpVisitorId', randomId)
    return randomId
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({ isOpen, onClose, userEmail }) => {
  const [phoneInput, setPhoneInput] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [isOtpSent, setIsOtpSent] = useState(false)
  const [canResend, setCanResend] = useState(true)
  const [resendTimer, setResendTimer] = useState(0)
  const [resendCount, setResendCount] = useState(0)
  const [fingerprint, setFingerprint] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY)

  const { mutate: sendOTP, isLoading: isSendingOTP } = useGetOTP()
  const { mutate: verifyOTP, isLoading: isVerifyingOTP } = useVerifyOTP()

  useEffect(() => {
    if (!isOpen) return
    setFingerprint(getOrCreateVisitorFingerprint())
  }, [isOpen])

  const cleanedPhoneDigits = useMemo(() => phoneInput.replace(/[^\d]/g, ''), [phoneInput])
  const fullPhoneNumber = useMemo(
    () => `${selectedCountry.dialCode}${cleanedPhoneDigits}`,
    [selectedCountry.dialCode, cleanedPhoneDigits]
  )

  const formatDisplayPhone = (raw: string) => {
    const digitsOnly = raw.replace(/\D/g, '')
    if (digitsOnly.length > 6) return digitsOnly.match(/.{1,3}/g)?.join(' ') || raw
    return raw
  }

  const resetAll = () => {
    setPhoneInput('')
    setOtpCode('')
    setIsOtpSent(false)
    setCanResend(true)
    setResendTimer(0)
    setResendCount(0)
    setSelectedCountry(DEFAULT_COUNTRY)
  }

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [resendTimer])

  useEffect(() => {
    if (!isOpen) resetAll()
  }, [isOpen])

  const handleSendOTP = () => {
    const digits = cleanedPhoneDigits
    const minLength = 8
    const maxLength = 15
    if (!digits || digits.length < minLength || digits.length > maxLength) {
      message.error(`Please enter a valid phone number (${minLength}-${maxLength} digits)`)
      return
    }

    sendOTP(
      { phone: fullPhoneNumber, fp: fingerprint, email: userEmail || '', attempt: 1 },
      {
        onSuccess: () => {
          setIsOtpSent(true)
          setCanResend(false)
          setResendTimer(RESEND_TIMER_DURATION_SECONDS)
          message.success(`Verification code sent to ${selectedCountry.dialCode} ${formatDisplayPhone(phoneInput)}`)
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.message || error?.message || 'Failed to send OTP. Please try again.')
        },
      }
    )
  }

  const handleVerifyOTP = () => {
    const digits = otpCode.replace(/\D/g, '')
    if (!digits || digits.length !== 4) {
      message.error('Please enter a valid 4-digit code')
      return
    }

    verifyOTP(
      { phone: fullPhoneNumber, fp: fingerprint, otp: digits },
      {
        onSuccess: () => {
          message.success('Verification successful! You have received 5 free credits')
          onClose()
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.message || error?.message || 'Invalid OTP. Please try again.')
        },
      }
    )
  }

  const handleResendOTP = () => {
    if (!canResend) return
    if (resendCount >= MAX_RESEND_ATTEMPTS) {
      message.error('Maximum resend attempts reached. Please try again later.')
      return
    }

    sendOTP(
      { phone: fullPhoneNumber, fp: fingerprint, email: userEmail || '', attempt: 2 },
      {
        onSuccess: () => {
          setResendCount(resendCount + 1)
          setCanResend(false)
          setResendTimer(RESEND_TIMER_DURATION_SECONDS)
          message.success('New verification code sent')
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.message || error?.message || 'Failed to resend OTP. Please try again.')
        },
      }
    )
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
      centered
      style={{ borderRadius: '12px' }}
      styles={{
        body: { padding: '32px 24px' },
      }}
    >
      <div style={{ textAlign: 'center' }}>
        {/* Header with icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            background: 'var(--gradient-primary)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
          }}
        >
          <Gift size={32} color='white' />
        </div>

        {/* Title */}
        <Typography.Title level={3} style={{ margin: '0 0 8px 0', color: '#1F2937', fontWeight: 700 }}>
          Get 5 Free Credits
        </Typography.Title>

        <Typography.Text style={{ color: '#6B7280', fontSize: '16px', marginBottom: '24px', display: 'block' }}>
          Just by adding your phone number
        </Typography.Text>

        {/* Description */}
        <Typography.Paragraph
          style={{
            color: '#6B7280',
            fontSize: '14px',
            lineHeight: '1.5',
            margin: '0 0 32px 0',
            textAlign: 'center',
          }}
        >
          To keep your account secure and deliver your bonus, we need to verify your number. In return, enjoy{' '}
          <span style={{ fontWeight: 600, color: 'var(--color-primary-600)' }}>5 free credits</span> to explore our premium features.
        </Typography.Paragraph>

        {!isOtpSent ? (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            {/* Country and Phone Input */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <Select
                value={selectedCountry.dialCode}
                onChange={(value) => {
                  const country = COUNTRIES.find((c) => c.dialCode === value) || DEFAULT_COUNTRY
                  setSelectedCountry(country)
                }}
                style={{ width: '140px', height: '56px' }}
                size='large'
                options={COUNTRIES.map((country) => ({
                  value: country.dialCode,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{country.flag}</span>
                      <span>{country.dialCode}</span>
                    </div>
                  ),
                }))}
              />
              <Input
                prefix={<Phone size={16} color='#9CA3AF' />}
                placeholder='(555) 000-0000'
                value={phoneInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d]/g, '')
                  if (value.length <= 10) setPhoneInput(value)
                }}
                style={{ flex: 1, height: '56px' }}
                size='large'
              />
            </div>

            <Button
              type='primary'
              onClick={handleSendOTP}
              loading={isSendingOTP}
              block
              size='large'
              style={{
                background: 'var(--gradient-primary)',
                border: 'none',
                height: '48px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              Send OTP
            </Button>
          </Space>
        ) : (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            <Typography.Text style={{ color: '#6B7280', fontSize: '14px', textAlign: 'center', display: 'block' }}>
              Enter the 4-digit code sent to {selectedCountry.dialCode} {formatDisplayPhone(phoneInput)}
            </Typography.Text>

            <Input
              placeholder='0000'
              value={otpCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                if (value.length <= 4) setOtpCode(value)
              }}
              maxLength={4}
              size='large'
              style={{
                textAlign: 'center',
                letterSpacing: '0.3em',
                fontSize: '18px',
                height: '56px',
                borderRadius: '8px',
              }}
            />

            <Button
              type='primary'
              onClick={handleVerifyOTP}
              loading={isVerifyingOTP}
              icon={<CheckCircle size={18} />}
              block
              size='large'
              style={{
                background: 'var(--gradient-primary)',
                border: 'none',
                height: '48px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              Verify & Get Credits
            </Button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <div>
                <Button
                  type='link'
                  onClick={handleResendOTP}
                  disabled={!canResend || resendCount >= MAX_RESEND_ATTEMPTS}
                  style={{
                    padding: 0,
                    height: 'auto',
                    color: canResend && resendCount < MAX_RESEND_ATTEMPTS ? 'var(--color-primary-600)' : '#9CA3AF',
                  }}
                >
                  {resendCount >= MAX_RESEND_ATTEMPTS
                    ? 'Max attempts reached'
                    : canResend
                    ? 'Resend OTP'
                    : `Resend OTP in ${resendTimer}s`}
                </Button>
                {resendCount > 0 && resendCount < MAX_RESEND_ATTEMPTS && (
                  <Typography.Text style={{ display: 'block', fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                    {MAX_RESEND_ATTEMPTS - resendCount} attempt
                    {MAX_RESEND_ATTEMPTS - resendCount !== 1 ? 's' : ''} left
                  </Typography.Text>
                )}
              </div>
              <Button
                type='link'
                onClick={resetAll}
                style={{
                  padding: 0,
                  height: 'auto',
                  color: 'var(--color-primary-600)',
                }}
              >
                Change Phone Number
              </Button>
            </div>
          </Space>
        )}
      </div>
    </Modal>
  )
}

export default PhoneVerificationModal
